import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Clock, Send, ArrowLeft, ArrowRight, AlertCircle, CheckCircle2, Sparkles, Play } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';
import WinhouseAIFeedbackBadge from '@/components/shared/WinhouseAIFeedbackBadge';

const wordCount = (txt) => (txt || '').trim().split(/\s+/).filter(Boolean).length;
const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

/**
 * IELTS Writing Mock Test
 * - Task 1 → 20 minutes
 * - Task 2 → 40 minutes
 * - Sequential: user finishes Task 1 (or time elapses), then Task 2 starts.
 * - On finalize, both essays are submitted (with AI feedback) and a combined results screen renders.
 */
const IeltsWritingMockPage = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [moduleSel, setModuleSel] = useState('academic');
  const [session, setSession] = useState(null);
  const [activePrompt, setActivePrompt] = useState(null);
  const [activeTaskKey, setActiveTaskKey] = useState('task_1'); // 'task_1' | 'task_2'
  const [essays, setEssays] = useState({ task_1: '', task_2: '' });
  const [seconds, setSeconds] = useState({ task_1: 0, task_2: 0 });
  const [submittedIds, setSubmittedIds] = useState({});
  const [results, setResults] = useState(null);
  const [working, setWorking] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try { await ieltsApi.authMe(); if (mounted) setAuthChecked(true); }
      catch { if (mounted) { setAuthChecked(true); setNeedsLogin(true); } }
    })();
    return () => { mounted = false; };
  }, []);

  // Timer
  useEffect(() => {
    if (!session || results || !activePrompt) return;
    timerRef.current = setInterval(() => {
      setSeconds((s) => ({ ...s, [activeTaskKey]: (s[activeTaskKey] || 0) + 1 }));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [session, results, activePrompt, activeTaskKey]);

  const currentTaskCfg = session?.plan?.[activeTaskKey];
  const currentEssay = essays[activeTaskKey] || '';
  const currentSec = seconds[activeTaskKey] || 0;
  const currentRem = Math.max(0, (currentTaskCfg?.duration_seconds || 0) - currentSec);
  const currentWc = useMemo(() => wordCount(currentEssay), [currentEssay]);
  const target = currentTaskCfg?.word_min || (activeTaskKey === 'task_1' ? 150 : 250);
  const allTasks = Object.keys(session?.plan || {});

  const startMock = async () => {
    setWorking(true);
    try {
      const r = await ieltsApi.writingMockStart({ module: moduleSel, include_tasks: [1, 2] });
      setSession(r);
      setActiveTaskKey('task_1');
      // Load Task 1 prompt
      const t1 = r.plan.task_1;
      const p1 = await ieltsApi.writingMockPrompt(r.mock_session_id, t1.prompt_id);
      setActivePrompt(p1.prompt);
    } catch (e) { toast.error(e.message || 'Could not start mock'); }
    finally { setWorking(false); }
  };

  const submitCurrent = async ({ moveToNext = true } = {}) => {
    if (!session || !activePrompt) return;
    if (currentWc < 30 && moveToNext) {
      if (!window.confirm('You have fewer than 30 words. Submit anyway?')) return;
    }
    setWorking(true);
    try {
      const r = await ieltsApi.writingSubmitEssay({
        prompt_id: activePrompt.prompt_id,
        essay_text: currentEssay,
        seconds_spent: currentSec,
        is_mock: true,
        mock_session_id: session.mock_session_id,
        request_ai_feedback: false, // defer AI until finalize for speed
      });
      setSubmittedIds((m) => ({ ...m, [activeTaskKey]: r.submission_id }));
      toast.success(`Saved ${activeTaskKey === 'task_1' ? 'Task 1' : 'Task 2'} response`);

      if (moveToNext) {
        if (activeTaskKey === 'task_1' && session.plan.task_2) {
          const t2 = session.plan.task_2;
          const p2 = await ieltsApi.writingMockPrompt(session.mock_session_id, t2.prompt_id);
          setActivePrompt(p2.prompt);
          setActiveTaskKey('task_2');
        } else {
          await finalizeMock();
        }
      }
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setWorking(false); }
  };

  const finalizeMock = async () => {
    if (!session) return;
    setWorking(true);
    try {
      const r = await ieltsApi.writingMockFinalize(session.mock_session_id);
      // Trigger AI evaluation for each submission in parallel
      const subs = r.submissions || [];
      await Promise.all(subs.map(async (s) => {
        try { await ieltsApi.writingEvaluateSubmission(s.submission_id); } catch (e) { /* tolerate */ }
      }));
      // Re-fetch updated submissions
      const updated = await Promise.all(subs.map((s) => ieltsApi.writingGetSubmission(s.submission_id).catch(() => s)));
      setResults({ ...r, submissions: updated });
    } catch (e) { toast.error(e.message || 'Finalize failed'); }
    finally { setWorking(false); }
  };

  if (!authChecked) {
    return (<div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-red-700" /></div>);
  }
  if (needsLogin) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center bg-white border border-gray-200 rounded-2xl p-8">
            <h1 className="text-2xl font-bold mb-2">Sign in to take the mock</h1>
            <Button onClick={() => goToIeltsAuth('/ielts/writing/mock')} className="bg-black text-white rounded-full px-6 h-11">
              Sign in with Google
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ielts-writing-mock">
      <Seo title="IELTS Writing Mock · Winhouse" canonical="/ielts/writing/mock" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 pt-24 sm:pt-28 pb-8 w-full">

        {/* Pre-start screen */}
        {!session && !results && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-10 max-w-2xl mx-auto" data-testid="writing-mock-start">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Writing Mock Test</div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">Full Writing Mock — 60 minutes</h1>
            <p className="text-gray-600 mb-5">Two tasks back-to-back, just like the real CBT exam.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Task 1</div>
                <div className="font-semibold">20 minutes · 150+ words</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Task 2</div>
                <div className="font-semibold">40 minutes · 250+ words</div>
              </div>
            </div>
            <div className="mb-6">
              <label className="text-xs uppercase tracking-wider text-gray-500 block mb-1.5">IELTS Module</label>
              <div className="flex gap-2">
                {['academic', 'general'].map((m) => (
                  <button key={m} onClick={() => setModuleSel(m)}
                    className={`px-5 h-10 rounded-full text-sm font-medium border transition-colors ${
                      moduleSel === m ? 'bg-black text-white border-black' : 'bg-white text-black border-gray-200 hover:border-black'
                    }`} data-testid={`writing-mock-module-${m}`}>{m === 'academic' ? 'Academic' : 'General Training'}</button>
                ))}
              </div>
            </div>
            <Button onClick={startMock} disabled={working}
              className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold rounded-full h-12 px-7 text-base"
              data-testid="writing-mock-begin">
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Begin Mock Test
            </Button>
          </div>
        )}

        {/* Live mock screen */}
        {session && !results && activePrompt && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/ielts/writing')} className="rounded-full">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Exit
                </Button>
                <Badge variant="outline" className="capitalize">{session.module}</Badge>
                <Badge className="bg-black text-white">{activeTaskKey === 'task_1' ? 'Task 1 · 20 min' : 'Task 2 · 40 min'}</Badge>
                {allTasks.map((k) => (
                  <Badge key={k} variant={submittedIds[k] ? 'default' : 'outline'}
                    className={submittedIds[k] ? 'bg-green-100 text-green-700 border-green-300' : ''}>
                    {submittedIds[k] ? <CheckCircle2 className="h-3 w-3 mr-1 inline" /> : null}
                    {k.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
              <div className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-mono tabular-nums text-sm ${
                currentRem === 0 ? 'bg-red-50 text-red-700 border border-red-200'
                : currentRem < 120 ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : 'bg-white text-black border border-gray-200'
              }`} data-testid="writing-mock-timer">
                <Clock className="h-4 w-4" /> {fmt(currentRem)}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 max-h-[calc(100vh-220px)] overflow-auto"
                data-testid="writing-mock-prompt-pane">
                <div className="text-xs uppercase tracking-[0.18em] text-red-700 font-semibold mb-3">
                  {activePrompt.module} · Task {activePrompt.task_number}
                  {activePrompt.chart_type ? <span className="ml-2 text-gray-500 normal-case tracking-normal">· {activePrompt.chart_type}</span> : null}
                </div>
                <h2 className="text-xl sm:text-2xl font-bold leading-snug mb-3">
                  {activePrompt.task_title || (activePrompt.task_type || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </h2>
                {activePrompt.instructions && (
                  <p className="text-sm text-gray-600 mb-4 italic">{activePrompt.instructions}</p>
                )}
                {activePrompt.image_url ? (
                  <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-2 sm:p-3">
                    <img src={activePrompt.image_url} alt={activePrompt.task_title || 'Task 1 visual'}
                      className="w-full h-auto max-h-[420px] object-contain mx-auto"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  </div>
                ) : null}
                <div className="whitespace-pre-wrap text-[15px] leading-7 font-serif">{activePrompt.prompt_text}</div>
              </section>

              <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 flex flex-col"
                data-testid="writing-mock-editor-pane">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="font-bold text-lg">Your response</h3>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-mono tabular-nums ${
                    currentWc >= target ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`} data-testid="writing-mock-wc">{currentWc} / {target} words</span>
                </div>
                <textarea
                  value={currentEssay}
                  onChange={(e) => setEssays((m) => ({ ...m, [activeTaskKey]: e.target.value }))}
                  rows={18}
                  className="flex-1 w-full resize-y border border-gray-200 rounded-xl p-4 text-[15px] leading-7 font-serif focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none"
                  data-testid="writing-mock-editor"
                />
                {currentRem === 0 && (
                  <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">
                    <AlertCircle className="h-4 w-4" /> Time is up for this task — please submit and move on.
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button onClick={() => submitCurrent({ moveToNext: true })} disabled={working}
                    className="bg-black text-white rounded-full px-6 h-11" data-testid="writing-mock-submit">
                    {working ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      : activeTaskKey === 'task_2' ? <Send className="h-4 w-4 mr-1.5" />
                      : <ArrowRight className="h-4 w-4 mr-1.5" />}
                    {activeTaskKey === 'task_2' ? 'Submit Mock & Get Feedback' : 'Save & Continue to Task 2'}
                  </Button>
                </div>
              </section>
            </div>
          </>
        )}

        {/* Results screen */}
        {results && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8" data-testid="writing-mock-results">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-red-700" />
                <h2 className="text-2xl font-bold">Mock Test Complete</h2>
              </div>
              <WinhouseAIFeedbackBadge model="claude-sonnet-4-5" />
            </div>
            <p className="text-gray-600 mb-6">
              Average band across both tasks: <span className="font-bold text-black text-lg">{results.average_band ?? '—'}</span>
            </p>
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              {(results.submissions || []).map((s) => {
                const ai = s.ai_feedback || {};
                const bands = ai.bands || {};
                return (
                  <div key={s.submission_id} className="border border-gray-200 rounded-2xl p-5">
                    <div className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-1">
                      Task {s.task_number} · {s.module}
                    </div>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-4xl font-bold text-black">{s.estimated_band_overall ?? '—'}</span>
                      <span className="text-xs text-gray-500 uppercase tracking-wider">overall band</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">TA<div className="text-base font-bold text-black">{bands.task_achievement ?? '—'}</div></div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">CC<div className="text-base font-bold text-black">{bands.coherence_cohesion ?? '—'}</div></div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">LR<div className="text-base font-bold text-black">{bands.lexical_resource ?? '—'}</div></div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">GRA<div className="text-base font-bold text-black">{bands.grammatical_range_accuracy ?? '—'}</div></div>
                    </div>
                    {ai.summary && <p className="text-sm text-gray-700 leading-6">{ai.summary}</p>}
                    <div className="mt-3">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/ielts/writing/dashboard`)}>
                        See full feedback in dashboard
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => navigate('/ielts/writing/dashboard')} className="bg-black text-white rounded-full px-6">
                Open Dashboard
              </Button>
              <Button variant="outline" onClick={() => { setSession(null); setResults(null); setEssays({task_1:'', task_2:''}); setSeconds({task_1:0, task_2:0}); setSubmittedIds({}); }}
                className="rounded-full px-6">
                Try another mock
              </Button>
            </div>
            <WinhouseAIFeedbackBadge variant="footer" model="claude-sonnet-4-5" />
          </div>
        )}
      </main>
    </div>
  );
};
export default IeltsWritingMockPage;
